import { api } from "../../api/apiSlice";

// Sales Monitoring (enquiry -> approval -> PDF quotation -> confirm ->
// guard deployment/installation) - React port of the Flutter-only module,
// against TexasAlarms.Api's already-complete src/Sales module. Follows the
// exact query/mutation/tag convention established by features/pettycash/
// pettyCashApi.js. Upcoming Deployments is wrapped in features/guarding/
// guardingApi.js instead (Phase 1, 2026-09-02) - it's its own module now,
// gated by DEPLOYMENT permissions rather than Sales.* ones; only the
// pre-existing setGuardDeployment/clearGuardDeployment mutations below are
// still shared/reused from here.
export const salesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Enquiries ----
    // People an order can be booked FOR (Batch148). Narrow id/name/designation
    // list, gated by Sales.BookForOthers - deliberately not the Users module's
    // own list, which needs Admin.ManageUsers.
    listSalespeople: builder.query({
      query: () => ({ url: "/sales/salespeople" }),
    }),
    createEnquiry: builder.mutation({
      query: (body) => ({ url: "/sales/enquiries", method: "POST", data: body }),
      invalidatesTags: [{ type: "SalesEnquiry", id: "LIST" }],
    }),
    getEnquiry: builder.query({
      query: (recordGuid) => ({ url: `/sales/enquiries/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "SalesEnquiry", id: recordGuid }],
    }),
    // Batch166 - quotations, LPOs, stock-outs, technical work and guard
    // assignments hanging off an enquiry and its order. Refreshes with the
    // enquiry and with any quotation change (linking one adds a row).
    getSalesLinkedDocuments: builder.query({
      query: (enquiryRecordGuid) => ({ url: `/sales/enquiries/${enquiryRecordGuid}/linked-documents` }),
      providesTags: (result, error, enquiryRecordGuid) => [
        { type: "SalesEnquiry", id: enquiryRecordGuid },
        { type: "Quotation", id: "LIST" },
      ],
    }),
    listEnquiries: builder.query({
      query: (params) => ({ url: "/sales/enquiries", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((e) => ({ type: "SalesEnquiry", id: e.recordGuid })),
              { type: "SalesEnquiry", id: "LIST" },
            ]
          : [{ type: "SalesEnquiry", id: "LIST" }],
    }),
    // Discount on the quotation (Batch147). Either a percentage or a flat
    // amount - the server rejects both at once. Sending both null clears it.
    // The discount reduces the VAT-able value, so the server recomputes
    // VatAmount/TotalAmount and returns the updated enquiry.
    setEnquiryDiscount: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/enquiries/${recordGuid}/discount`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),
    approveEnquiry: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/enquiries/${recordGuid}/approve`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),
    rejectEnquiry: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({
        url: `/sales/enquiries/${recordGuid}/reject`,
        method: "POST",
        data: { rejectionReason },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),
    confirmEnquiry: builder.mutation({
      query: (recordGuid) => ({ url: `/sales/enquiries/${recordGuid}/confirm`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    markEnquiryLost: builder.mutation({
      query: ({ recordGuid, reason }) => ({ url: `/sales/enquiries/${recordGuid}/lost`, method: "POST", data: { reason } }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),
    postponeEnquiry: builder.mutation({
      query: ({ recordGuid, remark, followUpDate }) => ({
        url: `/sales/enquiries/${recordGuid}/postpone`,
        method: "POST",
        data: { remark, followUpDate },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),
    reviveEnquiry: builder.mutation({
      query: (recordGuid) => ({ url: `/sales/enquiries/${recordGuid}/revive`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),
    resubmitEnquiry: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/enquiries/${recordGuid}/resubmit`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesEnquiry", id: recordGuid },
        { type: "SalesEnquiry", id: "LIST" },
      ],
    }),

    // ---- Direct Confirmed Order ----
    createConfirmedOrder: builder.mutation({
      query: (body) => ({ url: "/sales/orders/confirmed", method: "POST", data: body }),
      invalidatesTags: [{ type: "SalesEnquiry", id: "LIST" }, { type: "SalesOrder", id: "LIST" }],
    }),

    // ---- Quotation ----
    // `brochures` (2026-09-09) are optional product brochures emailed alongside
    // the quotation. They are pass-through only - never stored - so they apply
    // to this send alone and a later Resend carries the quotation by itself.
    // The field name must stay `brochures` to bind to the controller's
    // IFormFileCollection.
    uploadQuotation: builder.mutation({
      query: ({ recordGuid, file, brochures = [] }) => {
        const formData = new FormData();
        formData.append("file", file, "quotation.pdf");
        brochures.forEach((b) => formData.append("brochures", b, b.name));
        return { url: `/sales/enquiries/${recordGuid}/quotation`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "SalesEnquiry", id: recordGuid }],
    }),
    resendQuotation: builder.mutation({
      query: (recordGuid) => ({ url: `/sales/enquiries/${recordGuid}/quotation/resend`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [{ type: "SalesEnquiry", id: recordGuid }],
    }),
    // Authenticated PDF - fetched as a Blob (axiosBaseQuery's responseType
    // passthrough), same discipline as Petty Cash's receipt content.
    getQuotationPdfContent: builder.query({
      query: (recordGuid) => ({ url: `/sales/enquiries/${recordGuid}/quotation.pdf`, responseType: "blob" }),
    }),

    // ---- Orders ----
    listOrders: builder.query({
      query: (params) => ({ url: "/sales/orders", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((o) => ({ type: "SalesOrder", id: o.recordGuid })),
              { type: "SalesOrder", id: "LIST" },
            ]
          : [{ type: "SalesOrder", id: "LIST" }],
    }),
    getOrder: builder.query({
      query: (recordGuid) => ({ url: `/sales/orders/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "SalesOrder", id: recordGuid }],
    }),
    closeOrder: builder.mutation({
      query: ({ recordGuid, reason }) => ({ url: `/sales/orders/${recordGuid}/close`, method: "POST", data: { reason } }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    // Batch163 - discount on an OPEN order (same arithmetic as the enquiry's).
    setOrderDiscount: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/orders/${recordGuid}/discount`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    // Batch162. The API also closes the order's unfinished technical work in
    // the same transaction, so those caches are stale afterwards too.
    cancelOrder: builder.mutation({
      query: ({ recordGuid, reason }) => ({ url: `/sales/orders/${recordGuid}/cancel`, method: "POST", data: { reason } }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
        "TechnicalWork",
        "TechnicalWorkActivity",
      ],
    }),
    // Commission (2026-09-08). Endpoints already existed - only the UI was
    // missing, having been left out earlier while the flow was undecided.
    // "Paid" is not a status column: App_SalesOrderCommission either has a
    // row for the order or it does not, and the API derives
    // IsCommissionPaid = CommissionAmount.HasValue from exactly that. So
    // recording a commission marks it paid and Reverse deletes the row -
    // there is no separate flag to set, and no new status value was needed.
    upsertCommission: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/orders/${recordGuid}/commission`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    deleteCommission: builder.mutation({
      query: (recordGuid) => ({ url: `/sales/orders/${recordGuid}/commission`, method: "DELETE" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    setGuardDeployment: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/orders/${recordGuid}/guard-deployment`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    clearGuardDeployment: builder.mutation({
      query: (recordGuid) => ({ url: `/sales/orders/${recordGuid}/guard-deployment`, method: "DELETE" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    updateDeploymentLine: builder.mutation({
      query: ({ recordGuid, lineId, ...body }) => ({
        url: `/sales/orders/${recordGuid}/guard-deployment-lines/${lineId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),
    setInstallation: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/sales/orders/${recordGuid}/installation`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "SalesOrder", id: recordGuid },
        { type: "SalesOrder", id: "LIST" },
      ],
    }),

    // ---- Guard Service Types (global, read-only here - no CRUD UI anywhere) ----
    listGuardServiceTypes: builder.query({
      query: (params) => ({ url: "/masters/guard-service-types", params }),
      providesTags: [{ type: "GuardServiceType", id: "LIST" }],
    }),

    // ---- Summary (Dashboard) ----
    getSalesSummary: builder.query({
      query: (params) => ({ url: "/sales/summary", params }),
    }),

    // ---- Client Visits (Batch182) ----
    // The web side is read-only on purpose: visits are recorded in the field
    // from the Flutter app, where the QR scanner and the GPS gate live. This
    // is the reporting end of that - no create/edit endpoints are wired here.
    // Own-visits-only unless the caller holds Sales.ViewAllClientVisits, which
    // the server decides; these queries just pass the filters through.
    listClientVisits: builder.query({
      query: (params) => ({ url: "/sales/client-visits", params }),
      providesTags: [{ type: "SalesClientVisit", id: "LIST" }],
    }),
    getClientVisit: builder.query({
      query: (recordGuid) => ({ url: `/sales/client-visits/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "SalesClientVisit", id: recordGuid }],
    }),
    getClientVisitSummary: builder.query({
      query: (params) => ({ url: "/sales/client-visits/summary", params }),
      providesTags: [{ type: "SalesClientVisit", id: "LIST" }],
    }),
    // Batch203 - visits per person per source per day for one month ({ year, month, branchId, source }).
    // Sources: SALES (client visit report), COLLECTIONS (no-payment "Visit" entries), TECHNICAL (service reports).
    getClientVisitMonthGrid: builder.query({
      query: (params) => ({ url: "/sales/client-visits/month-grid", params }),
      providesTags: [{ type: "SalesClientVisit", id: "LIST" }],
    }),
    // Batch203 - the visits behind a grid count ({ dateFrom, dateTo (exclusive), branchId, source, personKey }).
    listVisitActivity: builder.query({
      query: (params) => ({ url: "/sales/client-visits/activity", params }),
      providesTags: [{ type: "SalesClientVisit", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateEnquiryMutation,
  useListSalespeopleQuery,
  useGetEnquiryQuery,
  useGetSalesLinkedDocumentsQuery,
  useListEnquiriesQuery,
  useApproveEnquiryMutation,
  useSetEnquiryDiscountMutation,
  useRejectEnquiryMutation,
  useConfirmEnquiryMutation,
  useMarkEnquiryLostMutation,
  usePostponeEnquiryMutation,
  useReviveEnquiryMutation,
  useResubmitEnquiryMutation,
  useCreateConfirmedOrderMutation,
  useUploadQuotationMutation,
  useResendQuotationMutation,
  useLazyGetQuotationPdfContentQuery,
  useListOrdersQuery,
  useLazyListOrdersQuery,
  useGetOrderQuery,
  useCloseOrderMutation,
  useCancelOrderMutation,
  useSetOrderDiscountMutation,
  useUpsertCommissionMutation,
  useDeleteCommissionMutation,
  useSetGuardDeploymentMutation,
  useClearGuardDeploymentMutation,
  useUpdateDeploymentLineMutation,
  useSetInstallationMutation,
  useListGuardServiceTypesQuery,
  useGetSalesSummaryQuery,
  useListClientVisitsQuery,
  useGetClientVisitQuery,
  useGetClientVisitSummaryQuery,
  useGetClientVisitMonthGridQuery,
  useListVisitActivityQuery,
} = salesApi;
